import { Stack, Typography } from '@mui/material';
import type { Account } from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';
import { useCallback } from 'react';

import { CloudProviderIndicator } from '@/components/cloudProviderIndicator';
import { Loader } from '@/components/layout/loading';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import { ReadMoreLink } from '@/components/shared/readMoreLink';
import { Wizard } from '@/components/wizard/Wizard';
import { docsBaseUrl } from '@/contexts/environment';
import { useDAL } from '@/contexts/useDAL';
import type { projectDalFactory } from '@/data/dal/dalFactory';

import {
  AwsRestoreAccountConfigFlow,
  GcpRestoreAccountConfigFlow,
  type RestoreAccountConfigState,
} from '../restoreAccountConfigFlow';

const onAwsSave = async (
  dal: ReturnType<typeof projectDalFactory>,
  state: RestoreAccountConfigState
) => {
  await dal.cloudAccounts.restore.connectivity.update(state.account.id, {
    aws: state.config.config.aws,
  });
};

const onGcpSave = async (
  dal: ReturnType<typeof projectDalFactory>,
  state: RestoreAccountConfigState
) => {
  await dal.cloudAccounts.restore.connectivity.update(state.account.id, {
    gcp: state.config.config.gcp,
  });
};

export function RestoreServerConnectivityPanel({
  account,
  onUpdate,
  onClose,
}: {
  account: Account;
  onUpdate: () => Promise<void>;
  onClose: () => void;
}) {
  const dal = useDAL();
  const {
    data: config,
    isLoading,
    mutate,
  } = dal.cloudAccounts.restore.connectivity.useConnectivity(account.id);

  const onSave = useCallback(
    async (state: RestoreAccountConfigState) => {
      if (state.account.cloudProvider === Provider.Aws) {
        await onAwsSave(dal, state);
      } else if (state.account.cloudProvider === Provider.Gcp) {
        await onGcpSave(dal, state);
      }
      await onUpdate();
      await mutate();
      return true;
    },
    [dal, onUpdate, mutate]
  );

  const description =
    account.cloudProvider === Provider.Aws
      ? 'Restore operations run on a temporary server. Before creating it, Eon selects an availability zone and security group based on the restore destination and resource type. If your default VPC resources have outbound limitations or were deleted, you’ll need to customize the connectivity.'
      : 'Restore operations run on a temporary server. Before creating it, Eon selects a subnet based on the restore destination and resource type. If your default network resources have outbound limitations or were deleted, you’ll need to customize the connectivity.';

  if (isLoading || !config) {
    return <Loader />;
  }

  return (
    <PanelWrapper
      testIdPrefix='restore-account-config'
      header={{
        title: 'Edit restore server connectivity',
        subtitle: (
          <Stack className='px-[20px]' gap='24px'>
            <CloudProviderIndicator
              cloudProvider={account.cloudProvider}
              text={account.providerAccountId}
            />
            <Stack gap='12px' className='mb-[24px]'>
              <Typography className='max-w-[680px]' variant='body1'>
                {description}{' '}
                <ReadMoreLink
                  href={`${docsBaseUrl}/user-guide/restoring/restore-accounts/about-restore-accounts#the-restore-server-and-its-network-connectivity`}
                />
              </Typography>
            </Stack>
          </Stack>
        ),
        onBack: onClose,
        onClose: onClose,
      }}
    >
      <Wizard<RestoreAccountConfigState>
        hideBackArrow={true}
        onAbort={onClose}
        onFinish={onSave}
        flow={[
          account.cloudProvider === Provider.Aws
            ? AwsRestoreAccountConfigFlow
            : GcpRestoreAccountConfigFlow,
        ]}
        initialState={{
          account,
          config: { ...config.restoreAccountConfig },
        }}
      />
    </PanelWrapper>
  );
}
